import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Stack,
    alpha
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { usePermissions } from "../../provider/PermissionProvider";
import { LsButton } from "../../core";

const NoAccessPage = () => {
    const {
        permissions,
        provisioning,
        syncAndReload,
        syncAttempts,
        maxSyncAttempts,
        hasExhaustedRetries,
    } = usePermissions();

    const [isRetrying, setIsRetrying] = useState(false);
    const hasSyncedRef = useRef(false);

    useEffect(() => {
        if (!hasSyncedRef.current && !hasExhaustedRetries) {
            hasSyncedRef.current = true;
            syncAndReload();
        }
    }, [syncAndReload, hasExhaustedRetries]);

    const handleRetry = async () => {
        if (hasExhaustedRetries) return;
        setIsRetrying(true);
        try {
            await syncAndReload();
        } finally {
            setIsRetrying(false);
        }
    };

    const handleContactSupport = () => {
        window.location.href = "mailto:kpapasso@legalstream.org?subject=Access%20Request&body=Hi,%20I%20need%20access%20to%20the%20reporting%20dashboard.";
    };

    // =============== STYLES ===============
    const containerStyles = {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    };

    const cardStyles = {
        width: "100%",
        maxWidth: 440,
        p: { xs: 4, sm: 5 },
        borderRadius: 3,
        boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "divider",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            bgcolor: "primary.main",
        }
    };

    const iconContainerStyles = {
        width: 72,
        height: 72,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 3,
    };

    // =============== STATE 1: ALL RETRIES EXHAUSTED ===============
    if (hasExhaustedRetries) {
        return (
            <Box sx={containerStyles}>
                <Paper sx={cardStyles}>
                    <Box sx={{
                        ...iconContainerStyles,
                        bgcolor: alpha("#ff4444", 0.1),
                        color: "error.main",
                    }}>
                        <LockOutlinedIcon sx={{ fontSize: 36 }} />
                    </Box>

                    <Typography variant="h5" fontWeight={600} gutterBottom>
                        Access Not Available
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                        Your account doesn't have access to this dashboard yet. 
                        Please request access from your administrator.
                    </Typography>

                    <Box sx={{
                        bgcolor: alpha("#2196f3", 0.05),
                        p: 3,
                        borderRadius: 2,
                        mb: 4,
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        textAlign: "left",
                    }}>
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>
                            How to get access:
                        </Typography>
                        <Stack spacing={1}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                <Box sx={{ color: "primary.main", mt: 0.5 }}>1.</Box>
                                <Typography variant="body2">Contact your manager or team lead</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                <Box sx={{ color: "primary.main", mt: 0.5 }}>2.</Box>
                                <Typography variant="body2">Request access to LegalStream Reporting</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                <Box sx={{ color: "primary.main", mt: 0.5 }}>3.</Box>
                                <Typography variant="body2">They will assign the required permissions</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Stack spacing={2}>
                        <LsButton
                            variant="contained"
                            size="large"
                            startIcon={<EmailIcon />}
                            onClick={handleContactSupport}
                            fullWidth
                            sx={{ py: 1.5 }}
                        >
                            Request Access
                        </LsButton>
                        <LsButton
                            variant="outlined"
                            size="large"
                            onClick={() => window.location.reload()}
                            fullWidth
                            sx={{ py: 1.5 }}
                        >
                            Refresh Page
                        </LsButton>
                    </Stack>
                </Paper>
            </Box>
        );
    }

    // =============== STATE 2: CHECKING/LOADING ===============
    if (provisioning || isRetrying) {
        return (
            <Box sx={containerStyles}>
                <Paper sx={cardStyles}>
                    <Box sx={{
                        ...iconContainerStyles,
                        bgcolor: alpha("#2196f3", 0.1),
                        color: "primary.main",
                    }}>
                        <CircularProgress 
                            size={40} 
                            thickness={4}
                            sx={{ color: "primary.main" }}
                        />
                    </Box>

                    <Typography variant="h5" fontWeight={600} gutterBottom>
                        Checking Permissions
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {isRetrying
                            ? "Verifying if access has been granted..."
                            : "Setting up your access permissions..."
                        }
                    </Typography>

                    <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        gap: 1,
                        color: "text.secondary",
                        fontSize: "0.875rem",
                    }}>
                        <HourglassEmptyIcon fontSize="small" />
                        <Typography variant="caption">
                            This usually takes just a few seconds
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }

    // =============== STATE 3: ACCESS GRANTED ===============
    if (permissions && permissions.length > 0) {
        return (
            <Box sx={containerStyles}>
                <Paper sx={cardStyles}>
                    <Box sx={{
                        ...iconContainerStyles,
                        bgcolor: alpha("#4caf50", 0.1),
                        color: "success.main",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                            "0%": { transform: "scale(1)" },
                            "50%": { transform: "scale(1.05)" },
                            "100%": { transform: "scale(1)" },
                        }
                    }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />
                    </Box>

                    <Typography variant="h5" fontWeight={600} gutterBottom color="success.main">
                        Access Granted
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                        Your access has been verified successfully. Redirecting to dashboard...
                    </Typography>

                    <CircularProgress 
                        size={24} 
                        sx={{ color: "success.main" }}
                    />
                </Paper>
            </Box>
        );
    }

    // =============== STATE 4: NO ACCESS YET (CAN RETRY) ===============
    return (
        <Box sx={containerStyles}>
            <Paper sx={cardStyles}>
                <Box sx={{
                    ...iconContainerStyles,
                    bgcolor: alpha("#ff9800", 0.1),
                    color: "warning.main",
                }}>
                    <PersonOutlineIcon sx={{ fontSize: 36 }} />
                </Box>

                <Typography variant="h5" fontWeight={600} gutterBottom>
                    Pending Access
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                    Your access to the reporting dashboard is being reviewed. 
                    This is usually completed within a few minutes.
                </Typography>

                <Box sx={{
                    bgcolor: alpha("#ff9800", 0.05),
                    p: 3,
                    borderRadius: 2,
                    mb: 4,
                    textAlign: "left",
                }}>
                    <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
                        Common reasons for delay:
                    </Typography>
                    <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: "50%", 
                                bgcolor: alpha("#ff9800", 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75rem",
                                color: "warning.main",
                                fontWeight: 600,
                            }}>
                                1
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Manager approval pending
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: "50%", 
                                bgcolor: alpha("#ff9800", 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75rem",
                                color: "warning.main",
                                fontWeight: 600,
                            }}>
                                2
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Permissions being processed
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: "50%", 
                                bgcolor: alpha("#ff9800", 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75rem",
                                color: "warning.main",
                                fontWeight: 600,
                            }}>
                                3
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Account configuration in progress
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <Stack spacing={2} sx={{ mb: 2 }}>
                    <LsButton
                        variant="contained"
                        size="large"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetry}
                        disabled={provisioning || isRetrying}
                        fullWidth
                        sx={{ py: 1.5 }}
                    >
                        Check Access Status
                    </LsButton>
                    <LsButton
                        variant="outlined"
                        size="large"
                        startIcon={<EmailIcon />}
                        onClick={handleContactSupport}
                        fullWidth
                        sx={{ py: 1.5 }}
                    >
                        Contact Support
                    </LsButton>
                </Stack>

                {syncAttempts > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: "block",
                            textAlign: "center",
                            fontSize: "0.75rem",
                        }}
                    >
                        Checked {syncAttempts} time{syncAttempts !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default NoAccessPage;